import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, makeStyles, withStyles } from "@material-ui/core";
import { useTranslate } from "react-polyglot";
import lodash from "lodash";
import Grid from "@material-ui/core/Grid";
import Divider from "@material-ui/core/Divider";
import Chip from "@material-ui/core/Chip";
import clsx from "clsx";
import ToggleButton from "../../../../SharedComponents/ToggleButton.jsx";
import FlexboxVertical from "../../../../SharedComponents/FlexboxVertical.jsx";
import FlexboxHorizontal from "../../../../SharedComponents/FlexboxHorizontal.jsx";
import { DEFAULT_LEARNED_PATCH } from "../../../../Providers/Data/LearnProcessProvider.jsx";
import { useCourseProvider } from "../../../../Providers/Data/CourseProvider.jsx";
import Question from "./SharedComponents/Question.jsx";
import Appendix from "./SharedComponents/Appendix.jsx";
import { delay } from "../../../../Utils/misc.js";
import CorrectAnswer from "./SharedComponents/CorrectAnswer.jsx";

const useStyles = makeStyles((theme) => ({
  sourceData: {
    marginTop: 20,
    padding: 20,
    justifyContent: "space-between",
  },
  validPairs: {
    marginTop: 20,
    padding: 20,
    flexGrow: 1,
    width: "100%",
    justifyContent: "flex-start",
  },
  validPair: {
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  validItem: {
    flexBasis: "48%",
  },
  sideBox: {
    display: "flex",
    flexDirection: "column",
    flexBasis: "48%",
    "&>*": {
      marginBottom: theme.spacing(1),
    },
  },
  leftBox: {
    borderRight: `1px solid ${theme.palette.grey[300]}`,
  },
  leftImage: {
    maxWidth: "100%",
    maxHeight: 120,
    objectFit: "contain",
    borderRadius: 12,
  },
  validImage: {
    flexBasis: "48%",
    maxWidth: "48%",
    border: "1px solid rgba(0, 0, 0, 0.23)",
    borderRadius: 18,
    backgroundColor: theme.palette.secondary.main,
    padding: 4,
    boxSizing: "border-box",
    overflow: "hidden",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
  },
  validImageImg: {
    maxWidth: "100%",
    maxHeight: 120,
    objectFit: "contain",
    borderRadius: 12,
  },
}));

const StyledChip = withStyles((theme) => ({
  root: {
    color: "white",
    fontSize: theme.typography.button.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    minHeight: 36,
    height: "unset",
    borderRadius: 18,
  },
  label: {
    whiteSpace: "normal",
    textAlign: "center",
    lineHeight: theme.typography.button.lineHeight,
    padding: 5,
    paddingLeft: 15,
    paddingRight: 15,
  },
}))(Chip);

/**
 * Показывает составление пар и передаёт неверные попытки общему обработчику правильного ответа.
 *
 * @param {ExerciseType8Legacy|ExerciseType8} exercise
 * @param {Function} setCheckButtonDisabled
 * @param {String} exerciseTitle
 * @param {MutableRefObject<Function>} checkAnswerRef
 * @param {Boolean} disabledChoice
 * @param {boolean} isHiddenCheckButton - Признак скрытия кнопки проверки (не используется в Exercise8, но передаётся родителем; деструктурируется, чтобы не попасть на DOM-элемент через rest).
 * @param {boolean} isExam - Признак экзамена (не используется в Exercise8, но передаётся родителем; деструктурируется, чтобы не попасть на DOM-элемент через rest).
 * @param {FnComponentArrayStringVoid} showRightAnswerWithComments - Показ комментария к ответу при тех же общих условиях, что и в упр. 11 (в экзамене — только при включённой опции showAnswerComments).
 * @param {Object} rest
 * @returns {*}
 * @constructor
 */
const Exercise8 = ({
  exercise,
  setCheckButtonDisabled,
  exerciseTitle,
  checkAnswerRef,
  disabledChoice,
  isHiddenCheckButton,
  isExam,
  showRightAnswerWithComments,
  ...rest
}) => {
  const classes = useStyles();
  const t = useTranslate();
  const { currentDeck } = useCourseProvider();
  const [resetState, setResetState] = useState(Date.now());
  const [leftRandomValues, setLeftRandomValues] = useState([]);
  const [rightRandomValues, setRightRandomValues] = useState([]);
  const [currentPair, setCurrentPair] = useState([null, null]);
  const [choicePairs, setChoicePairs] = useState(/** @type {Array<String[]>} */ []);
  const [validPairs, setValidPairs] = useState(/** @type {Array<String[] | null>} */ null);
  const checkFunctionHandleRef = useRef(0);
  const [error, setError] = useState(false);

  /** @type {ExerciseType8Legacy|ExerciseType8Answer} */
  const answer = exercise.answer || exercise;
  const pairs = answer.pairs;
  const extraWords = answer.extraWords;
  const isImageText = answer.pairType === "image-text";

  //reset state
  useEffect(() => {
    let leftSet = pairs.map((pair) => pair[0]);
    leftSet = lodash.shuffle(leftSet);
    let rightSet = pairs.map((pair) => pair[1]);
    if (extraWords) {
      rightSet.push(...extraWords);
    }
    rightSet = lodash.shuffle(rightSet);
    setLeftRandomValues(leftSet);
    setRightRandomValues(rightSet);
    setCurrentPair([null, null]);
    setChoicePairs([]);
    setValidPairs(null);
    setError(false);
  }, [pairs, resetState, extraWords]);

  // check current selected pair
  useEffect(() => {
    clearTimeout(checkFunctionHandleRef.current);
    if (!currentPair[0] || !currentPair[1]) {
      return;
    }
    checkFunctionHandleRef.current = setTimeout(() => {
      leftRandomValues.splice(currentPair[0].i, 1);
      rightRandomValues.splice(currentPair[1].i, 1);
      setLeftRandomValues([...leftRandomValues]);
      setRightRandomValues([...rightRandomValues]);

      setChoicePairs([...choicePairs, [currentPair[0].v, currentPair[1].v]]);
      setCurrentPair([null, null]);
    }, 700);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPair, exercise]);

  // update check button state
  useEffect(() => {
    if (!pairs) {
      return;
    }
    setCheckButtonDisabled(choicePairs.length !== pairs.length);
  }, [setCheckButtonDisabled, pairs, choicePairs]);

  checkAnswerRef.current = async () => {
    const notValidPairs = [];
    const newValidPairs = [];
    choicePairs.forEach((choiceItem) => {
      const searchResult = pairs.find((pair) => pair[0] === choiceItem[0] && pair[1] === choiceItem[1]);
      if (searchResult) {
        newValidPairs.push(searchResult);
      } else {
        notValidPairs.push(choiceItem);
      }
    });

    // в экзаменах ответы не показвааем
    if (!currentDeck.exam) {
      setValidPairs(newValidPairs);
      await delay(2000);
    } else {
      await delay(500);
    }

    const learned = pairs.length === newValidPairs.length;
    let resultString = "";
    if (notValidPairs.length > 0) {
      resultString = notValidPairs
        .map((item) => {
          const text = item.join("-");
          return text.replace(/,/g, "_");
        })
        .join(",");
    }

    // Комментарий к ответу: показываем при тех же общих условиях, что и в упр. 11
    // (в экзамене — только при включённой опции showAnswerComments)
    const comments = answer.comment ? [answer.comment] : [];

    // Общий обработчик считает неверные попытки и показывает пары после достижения лимита.
    showRightAnswerWithComments(
      learned ? null : <CorrectAnswer exercise={{ type: 8, data: exercise }} />,
      comments,
      resultString,
    );
    setResetState(Date.now());
    if (!currentDeck.exam && !learned) {
      return null;
    } else {
      return {
        ...DEFAULT_LEARNED_PATCH,
        learned,
        payload: { answerValue: resultString },
      };
    }
  };

  /** @param {Array} pair */
  const setColor = (pairItem) => {
    const searchResult = pairs.find((pair) => pair[0] === pairItem[0] && pair[1] === pairItem[1]);
    if (searchResult) {
      return "primary";
    } else {
      return "default";
    }
  };

  /** @param {Array} pair */
  const toggleButtonHandler = (pair) => () => {
    if (error) {
      return;
    }
    setCurrentPair(pair);
  };

  // функция возврата пар для нового выбора
  /**
   *  @param {Array} pair
   *  @param {number} i
   */
  const returnPair = (pair, i) => {
    setTimeout(() => {
      choicePairs.splice(i, 1);
      setChoicePairs([...choicePairs]);
      leftRandomValues.push(pair[0]);
      rightRandomValues.push(pair[1]);
      setLeftRandomValues([...leftRandomValues]);
      setRightRandomValues([...rightRandomValues]);
    }, 300);
  };

  return (
    <Box {...rest} data-component="Exercise8" display="flex" flexDirection="column" alignItems="center" height="100%">
      <Question
        exerciseQuestion={exercise.question}
        exerciseTitle={exerciseTitle}
        defaultTitle={t("Exercises.type8.title")}
      />
      {exercise.appendix && exercise.appendix.pdf && <Appendix pdfUrl={exercise.appendix.pdf.url} />}
      <Grid container className={classes.sourceData} direction="row">
        <Grid item data-tag="left side" className={classes.sideBox}>
          {leftRandomValues.map((v, i) => (
            <ToggleButton
              key={v + i}
              value={lodash.get(currentPair[0], "v") === v}
              onChange={toggleButtonHandler([{ v, i }, currentPair[1]])}
              color="primary"
              disabled={disabledChoice}
              error={error && lodash.get(currentPair[0], "v") === v}
            >
              {isImageText ? <img className={classes.leftImage} src={v} alt="" /> : v}
            </ToggleButton>
          ))}
        </Grid>
        <Divider orientation="vertical" flexItem />
        <Grid item data-tag="right side" className={classes.sideBox}>
          {rightRandomValues.map((v, i) => (
            <ToggleButton
              key={v + i}
              value={lodash.get(currentPair[1], "v") === v}
              onChange={toggleButtonHandler([currentPair[0], { v, i }])}
              color="primary"
              disabled={disabledChoice}
              error={error && lodash.get(currentPair[1], "v") === v}
            >
              {v}
            </ToggleButton>
          ))}
        </Grid>
      </Grid>
      <FlexboxVertical className={clsx(classes.sourceData, classes.validPairs)}>
        {choicePairs.map((pair, i) => (
          <FlexboxHorizontal key={pair[0] + pair[1]} className={classes.validPair}>
            {isImageText ? (
              <Box className={classes.validImage} onClick={() => returnPair(pair, i)}>
                <img className={classes.validImageImg} src={pair[0]} alt="" />
              </Box>
            ) : (
              <StyledChip
                onClick={() => returnPair(pair, i)}
                className={classes.validItem}
                label={pair[0]}
                color={validPairs ? (validPairs.length > 0 ? setColor(pair) : "default") : "secondary"}
                clickable
              />
            )}
            <StyledChip
              onClick={() => returnPair(pair, i)}
              className={classes.validItem}
              label={pair[1]}
              color={validPairs ? (validPairs.length > 0 ? setColor(pair) : "default") : "secondary"}
              clickable
            />
          </FlexboxHorizontal>
        ))}
      </FlexboxVertical>
    </Box>
  );
};

Exercise8.propTypes = {
  checkAnswerRef: PropTypes.object.isRequired,
  exercise: PropTypes.object.isRequired,
  exerciseTitle: PropTypes.string,
  setCheckButtonDisabled: PropTypes.func.isRequired,
  showRightAnswerWithComments: PropTypes.func,
  disabledChoice: PropTypes.bool,
  isHiddenCheckButton: PropTypes.bool,
  isExam: PropTypes.bool,
};

export default Exercise8;
